<template>
  <article class="subscription-request">
    <header class="subscription-request__header">
      <button class="button-transparent mr-auto" @click="opened = !opened">
        <div class="subscription-request__status" :class="{ '-success': request.confirmedAt != null }">
          <template v-if="request.confirmedAt != null">
            {{ $t("orderStatus.confirmed") }}
          </template>
          <template v-else>
            {{ $t("orderStatus.requested") }}
          </template>
        </div>

        <h2 v-if="plan" class="mv-0 ml-2 inline-flex subscription-request__heading">
          simcision {{ plan.label }} <template v-if="request.subscriptionId">— {{ $t("headings.renewal") }}</template>
        </h2>

        <span
          v-if="seats != null"
          class="ml-2 color-text-medium whitespace-nowrap"
        >
          {{ seats == Infinity ? $t("labels.unlimited") : seats }}
          {{ $t("words.user", seats) }}
        </span>

        <lucide-icon
          :name="opened ? 'chevron-up' : 'chevron-down'"
          class="subscription-request__icon ml-2"
          style="margin-bottom: -4px"
        />
      </button>

      <div class="flex gap-4 items-center justify-between">
        <span>
          <a
            v-if="request.confirmedAt == null"
            :href="acceptOfferMailtoLink"
            class="whitespace-nowrap"
          >
            {{ $t("actions.acceptOffer") }}
            <lucide-icon name="square-arrow-out-up-right" class="ml-2" />
          </a>
          <span v-else>
            {{ $t("labels.activatedAt", { date: formatDateString(request.startDate) }) }}
          </span>
        </span>

        <button
          type="button"
          class="button-icon"
          :aria-label="$t('actions.rejectOffer')"
          :title="$t('actions.rejectOffer')"
          @click="cancelSubscription()"
        >
          <lucide-icon name="trash-2" />
        </button>
      </div>
    </header>

    <div v-if="opened">
      <template v-if="request.subscriptionId != null">
        <h3 class="subscription-request__subheading">{{ $t("objectNames.subscription") }}</h3>
        <router-link
          :to="{ name: 'subscription-details', params: { subscriptionId: request.subscriptionId } }"
        >
          {{ $t("actions.viewSubscription") }}
        </router-link>
      </template>

      <h3 class="subscription-request__subheading">{{ $t("headings.details") }}</h3>
      <div class="column">
        <div>{{ $t("labels.countUsers", request.seats) }}</div>
        <div v-for="upgrade of request.upgrades">
          <div>{{ $t(`upgrades.${upgrade}.name`) }}</div>
        </div>
      </div>

      <h3 class="subscription-request__subheading">
        {{ $t("headings.subscriptionTime") }}
      </h3>
      <div>
        {{ $t("labels.licenseStartDate") }}:
        <template v-if="request.startDate == null">
          {{ $t("labels.orderDate") }}
        </template>
        <template v-else>
          {{ formatDateString(request.startDate) }}
        </template>
        <br />
        {{ $t("labels.licenseDuration") }}: {{ $t("count.years", request.years) }}
        <template v-if="request.renewAutomatically">
          <br>
          {{ $t("texts.renewsAutomatically") }}
        </template>
      </div>

      <h3 class="subscription-request__subheading">
        {{ $t("headings.requestedOn") }}
      </h3>
      <div>{{ formatDateString(request.created) }}</div>

      <h3 class="subscription-request__subheading">
        {{ $t("headings.billingAddress") }}
      </h3>
      <div>
        <template v-if="address">
          <div>{{ address.company }}</div>
          <div>{{ address.firstname }} {{ address.lastname }}</div>
          <div>{{ address.street }}</div>
          <div v-if="address.supplement">{{ address.supplement }}</div>
          <div>{{ address.zipcode }} {{ address.city }}</div>
          <div>{{ codeToName(address.country) }}</div>

          <div v-if="address.vatId" class="mt-2">
            {{ $t("labels.vatId") }}{{ $t(":") }} {{ address.vatId }}
          </div>
        </template>
        <p v-else class="message-info">
          {{ $t("errors.noAddress") }}
        </p>
      </div>

      <div class="mt-5">
        <button type="button" class="button button_secondary" @click="downloadQuote()" :disabled="downloadingQuote">
          {{ $t("actions.downloadQuote") }} <span class="ml-2">
            <Loading-spinner v-if="downloadingQuote" size="small" />
            <lucide-icon v-else name="download" />
          </span>
        </button>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { formatDateString } from "@/i18n";
import { useCountries } from "@/helpers/countries";
import { showConfirm, showError } from "@core/ui/alert/alert";
import { get } from "@/api/API";

import LucideIcon from "@core/ui/LucideIcon.vue";
import LoadingSpinner from "@core/ui/LoadingSpinner.vue";

import { usePlans } from "@manager/plans/plans.store";
import { useSubscriptions } from "@/api/subscriptions.store";

import type { LicensePlan } from "@manager/plans/plans.store";
import type { SubscriptionRequest } from "@/api/subscriptions.store";

const props = defineProps<{
  request: SubscriptionRequest;
  type?: "license" | "organization";
}>();
const emit = defineEmits<{
  (event: "update:request", request: SubscriptionRequest): void;
  (event: "remove:request", subscriptionId: number): void;
}>();

const { t } = useI18n();
const { codeToName } = useCountries();

const { plans } = usePlans();
const subscriptionStore = useSubscriptions();

const opened = ref(false);

const plan = computed((): LicensePlan | undefined => {
  return plans[props.request.plan];
});

const seats = computed(() => {
  if ("seats" in props.request) {
    if (props.request.seats === null) return Infinity;
    return props.request.seats ?? null;
  } else return null;
});

const address = computed(() => {
  return props.request?.billingAddress;
});

const acceptOfferMailtoLink = computed(() => {
  return `mailto:office@icondu.de?subject=${t("mails.acceptOffer.subject", { quoteId: props.request.lexofficeQuoteNumber })}&body=${t("mails.acceptOffer.body", { created: formatDateString(props.request.created), quoteId: props.request.lexofficeQuoteNumber })}`;
});

async function cancelSubscription() {
  if (
    (await showConfirm(
      t("questions.cancelSubscriptionRequest"),
      t("actions.cancelOrder")
    )) == false
  ) {
    return;
  }

  try {
    await subscriptionStore.cancelSubscriptionRequest(props.request.id);
    console.log("Subscription request canceled", props.request);
    emit("remove:request", props.request.id);
  } catch (e) {
    console.error(e);
    showError(e as Error);
  }
}

const downloadingQuote = ref(false);
async function downloadQuote() {
  try {
    downloadingQuote.value = true;
    const pdf = await get(`/v1/subscription-requests/${props.request.id}/download_quote`, {
      headers: {
        "accept": "application/octet-stream"
      },
      responseType: "blob"
    });
    const blob = new Blob([pdf], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.download = `simcision-quote-${props.request?.created}.pdf`;
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    const error = e as Error;
    console.warn(error);
    showError(error);
  } finally {
    downloadingQuote.value = false;
  }
}
</script>

<style lang="scss">
.subscription-request {
  box-shadow: 0 2px 10px var(--color-shadow-1);
  width: 100%;
  max-width: 75rem;
  min-width: 0;
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: 5px;
  background-color: var(--color-background-deep);
  padding: 1.25rem 1.5rem;
  container-type: inline-size;

  &__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    @container (max-width: 40rem) {
      gap: 0.5rem;
      flex-direction: column;
      align-items: stretch;
    }
  }
  &__heading {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-text-moderate);
  }

  &__icon {
    font-size: 1.25rem;
    color: var(--color-text-medium);
  }

  &__subheading {
    color: var(--color-text-medium);
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  &__status {
    position: relative;
    top: -2px;
    font-size: 1rem;
    font-weight: 400;
    display: inline-block;
    width: fit-content;
    padding: 0.25rem 0.5rem;
    border-radius: 50rem;
    background-color: var(--color-background);
    color: var(--color-text-moderate);
    box-shadow: 0 0 0 1px var(--color-border);

    &.-success {
      background-color: var(--color-success-light);
      color: var(--color-success-text);
      border: 1px solid var(--color-success-dark);
    }
  }
}
</style>
